import type { ReactElement } from 'react'
import React, { useCallback, useState } from 'react'
import { useForm } from 'react-hook-form'
import type { IRequestState } from '@/logic/requestStatus/interfaces'
import { makeAllFieldsTouched } from '@/utils/fields'
import { useMount } from '@/utils/hooks'
import CustomizedLeasingFormFields from './components/CustomizedLeasingFormFields'
import type { ICustomizedLeasingFormProps, ICustomizedLeasingWizardForm } from './interfaces'

function CustomizedLeasingForm(props: ICustomizedLeasingFormProps): ReactElement {
  const { onSubmit, initialValues, tooltipText, shouldDisableButton } = props

  const [requestState, setRequestState] = useState<IRequestState>({
    isLoading: false,
    isLoaded: false,
    isFailure: false,
  })

  const form = useForm<ICustomizedLeasingWizardForm>({
    mode: 'onTouched',
    defaultValues: initialValues,
  })

  const clearRequestState = () => {
    setRequestState({
      isFailure: false,
      isLoaded: false,
      isLoading: false,
    })
  }

  const handleSubmitAndTouchAllFields: Parameters<typeof form.handleSubmit>[0] = useCallback(
    async values => {
      setRequestState({
        isFailure: false,
        isLoaded: false,
        isLoading: true,
      })

      try {
        await onSubmit(values)
        makeAllFieldsTouched(form)

        setRequestState({
          isFailure: false,
          isLoaded: true,
          isLoading: false,
        })
      } catch (error) {
        setRequestState({
          isFailure: true,
          isLoaded: false,
          isLoading: false,
        })

        throw error
      }
    },
    [form, onSubmit],
  )

  const handleSubmitErrorAndTouchAllFields: Parameters<typeof form.handleSubmit>[1] = useCallback(() => {
    makeAllFieldsTouched(form)
  }, [form])

  useMount(clearRequestState, clearRequestState)

  return (
    <form className="wizard-form" onSubmit={form.handleSubmit(handleSubmitAndTouchAllFields, handleSubmitErrorAndTouchAllFields)}>
      <CustomizedLeasingFormFields
        requestState={requestState}
        tooltipText={tooltipText}
        shouldDisableButton={shouldDisableButton}
        form={form}
        regionSelectErrorMessage={props.regionSelectErrorMessage}
      />
    </form>
  )
}

export default CustomizedLeasingForm
